import { Box, Stack } from '@mui/material'
import Typography from '@mui/material/Typography'
import { APIError } from 'components/core/errors/api'
import { C1Link } from 'components/core/links/C1Link'
import { LoginButton, LoginCard, LoginLayout, LoginTitle } from 'components/core/login-helper'
import { TextField } from 'components/core/textfield'
import { PrivacyAndTerms } from 'components/pages/login/PrivacyAndTerms'
import { getTenant } from 'lib/actions/accounts'
import { AccountsReducer, initialAccountsState } from 'lib/reducers/accounts'
import { routes } from 'lib/routes'
import { Status } from 'lib/rpc'
import { useUnauthedClientRRPC } from 'lib/rpcts'
import { InstallationDomainInputAdornment } from 'lib/tenant'
import { useRouter } from 'next/router'
import { AccountsClient } from 'pbts/c1/api/accounts/v1/accounts.client'
import { useCallback, useEffect, useReducer, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

export interface LoginProps {
    externalSSO: 'none' | 'help' | 'docs'
    helpReturnURL: null | string
    docsLocationURL: null | string
}

const Login = ({ externalSSO, helpReturnURL, docsLocationURL }: LoginProps): React.ReactElement => {
    const intl = useIntl()
    const router = useRouter()
    const [accountsState, accountsDispatch] = useReducer(AccountsReducer, initialAccountsState)
    const accountsClient = useUnauthedClientRRPC<AccountsClient>(AccountsClient)

    const domainRef = useRef<HTMLInputElement>(null)
    const handleClick = useCallback(() => {
        if (domainRef === null) {
            return
        }
        const domain = domainRef.current?.value
        if (!domain) {
            return
        }
        getTenant(accountsDispatch, accountsClient, domain)
    }, [domainRef, accountsDispatch, accountsClient])

    useEffect(() => {
        if (accountsState.item.status === Status.OK) {
            const account = accountsState.item.item
            const installation = account.installation
            if (!installation) {
                return
            }
            const url = `https://${account.domain}.${installation.domain}:${installation.port}`
            const ux = new URL('/', url)

            switch (externalSSO) {
                case 'help':
                    ux.searchParams.append('external_sso', 'help')
                    ux.searchParams.append('return_to', helpReturnURL || '')
                    break
                case 'docs':
                    ux.searchParams.append('external_sso', 'docs')
                    ux.searchParams.append('location', docsLocationURL || '')
                // no default
            }
            router.replace(ux.toString())
        }
    }, [accountsState.item, router, helpReturnURL, externalSSO, docsLocationURL])

    return <LoginLayout>
        <LoginCard>
            <LoginTitle>
                <FormattedMessage defaultMessage="Sign in" id="component_login_title" />
            </LoginTitle>
            {accountsState.item.status === Status.ERROR && <APIError
                err={accountsState.item.error}
                title={
                    <FormattedMessage
                        id="component_login_lookup_error"
                        defaultMessage="Error occurred while signing in:"
                    />
                }
            />}
            <Stack spacing={2}>
                <TextField
                    inputProps={{ ref: domainRef }}
                    label={intl.formatMessage({
                        id: 'login_domain_title',
                        defaultMessage: 'Domain',
                    })}
                    required
                    id="domain"
                    name="domain"
                    autoComplete="domain"
                    autoFocus
                    InputProps={{
                        endAdornment: <InstallationDomainInputAdornment />,
                    }}
                />
                <LoginButton onClick={handleClick}>
                    <FormattedMessage id="component_login_button" defaultMessage="Sign in" />
                </LoginButton>
                <Stack direction="column" spacing={1} sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        <FormattedMessage
                            id="component_login_signup_prompt"
                            defaultMessage="Effortless identity security, built with {emoji} by a team you can trust."
                            values={{ emoji: '💟' }}
                        />
                    </Typography>
                    <Typography variant="caption">
                        <C1Link href={routes.accounts.signup}>
                            <FormattedMessage id="component_login_signup_link_text" defaultMessage="Sign up" />
                        </C1Link>
                    </Typography>
                </Stack>
            </Stack>
            <Box component="footer" sx={{ textAlign: 'center' }}>
                <PrivacyAndTerms />
            </Box>
        </LoginCard>
    </LoginLayout>
}

export default Login
